import React ,{ useEffect, useState} from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Sidebar from "../../components/Sidebar";
import { useNavigate, Link } from "react-router-dom";
import { stringify } from "querystring";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from 'react-helmet';
import { toast } from "react-toastify";
import ToastDefault from "../../components/Toast";
import { SettingAction } from "../../redux/actions/UserActions";
import { getAuth } from "../../utils/Helper";
import './setting.css';

type SeoContent = {
  meta_title: string;
  meta_description?: string;
  // Add more fields as needed
};

const Setting: React.FC = () => {

    const dispatch = useDispatch<any>();
    const [soeContent, setSoeContent] = useState<SeoContent | null>(null);
    const auth = getAuth();
    const [loadingM, setLoadingM] = useState(false);
    const { status, data, error } = useSelector((state: any) => state.settingState);

    // Initial state - always safe defaults
    const [formData, setFormData] = useState({
      display_name: auth?.display_name || "",
      timezone: auth?.timezone || "",
      language: auth?.language || "",
      email_notifications: auth?.email_notifications ?? false,
      in_app_notifications: auth?.in_app_notifications ?? false,
      sms_notifications: auth?.sms_notifications ?? false,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Validation function
    const validateForm = () => {
      const newErrors: Record<string, string> = {};
      if (!formData.display_name?.trim()) newErrors.display_name = "Display name is required.";
      if (!formData.timezone?.trim()) newErrors.timezone = "Time zone is required.";
      if (!formData.language?.trim()) newErrors.language = "Language is required.";
      return newErrors;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { id, value, type } = e.target;
      const isCheckbox = type === "checkbox";
      setFormData({
        ...formData,
        [id]: isCheckbox && e.target instanceof HTMLInputElement ? e.target.checked : value,
      });
    };

    const handleSubmit = (e: React.FormEvent) => {
        setLoadingM(true);
        e.preventDefault();
        const validationErrors = validateForm();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length === 0) {
            console.log("Settings saved:", formData);
             dispatch(SettingAction(formData));
            //alert("Settings updated successfully!");
        }else{
          setTimeout(() => {
            setLoadingM(false);
          }, 200);
        }
    };

  useEffect(() => {
    if (status) {

      localStorage.setItem("user-data", JSON.stringify(data?.data));
      console.log("Update Setting :", data);

      dispatch(SettingAction("RESET"));
      toast.success(data?.message);
      setLoadingM(false);

    }
    if (error) {
      dispatch(SettingAction("RESET"));
      setLoadingM(false);
    }
  }, [dispatch, status, error]);


  return (
    <>
    <Helmet>
      <title>{`${soeContent?.meta_title || 'Setting'}`}</title>
      <meta name="title" content={soeContent?.meta_title || 'E-learning Platform'} />
      <meta name="description" content={soeContent?.meta_description || 'Learn with curated e-learning resources'} />
      <meta name="keywords" content="e-learning, tech-cards, cheat-sheets, tutorials" />
    </Helmet>

      <Header />

      <div className="container py-5">
        <div className="row">
          <Sidebar />

          <div className="col-lg-9">
            <div className="main-content">
              <h2 className="mb-4">Account Settings</h2>
              <form onSubmit={handleSubmit}>
                {/* GENERAL SETTINGS */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5>General Settings</h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">Display Name</label>
                      <input
                        type="text"
                        id="display_name"   // ✅ changed to match state key
                        className={`form-control ${errors.display_name ? "is-invalid" : ""}`}
                        value={formData.display_name}
                        onChange={handleChange}
                      />
                      {errors.display_name && (
                        <div className="invalid-feedback">{errors.display_name}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Time Zone</label>
                      <select
                        id="timezone"
                        className={`form-select ${errors.timezone ? "is-invalid" : ""}`}
                        value={formData.timezone}
                        onChange={handleChange}
                      >
                        <option value="">Select a time zone</option>
                        <option>America/New_York</option>
                        <option>Europe/London</option>
                        <option>Asia/Kolkata</option>
                        <option>Australia/Sydney</option>
                      </select>
                      {errors.timezone && (
                        <div className="invalid-feedback">{errors.timezone}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Language</label>
                      <select
                        id="language"
                        className={`form-select ${errors.language ? "is-invalid" : ""}`}
                        value={formData.language}
                        onChange={handleChange}
                      >
                        <option value="">Select a language</option>
                        <option>English</option>
                        <option>Spanish</option>
                        <option>French</option>
                      </select>
                      {errors.language && (
                        <div className="invalid-feedback">{errors.language}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* NOTIFICATION PREFERENCES */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h5>Notification Preferences</h5>
                  </div>
                  <div className="card-body">
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="email_notifications"   // ✅ match state key
                        checked={formData.email_notifications}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">
                        Email Notifications
                        <div className="text-muted small">
                          Receive emails about new courses and special offers.
                        </div>
                      </label>
                    </div>

                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="in_app_notifications"   // ✅ match state key
                        checked={formData.in_app_notifications}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">
                        In-App Notifications
                        <div className="text-muted small">
                          Get alerts for assignment deadlines and course updates.
                        </div>
                      </label>
                    </div>

                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sms_notifications"   // ✅ match state key
                        checked={formData.sms_notifications}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">
                        SMS Notifications
                        <div className="text-muted small">
                          Receive important updates via text message.
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary width_200" disabled={loadingM} >
                  {loadingM ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Changing...
                    </>
                  ) : (
                    <>
                      Save Changes
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastDefault />
      <Footer />
    </>
  );
};

export default Setting;
